import { useState, useEffect, useCallback } from 'react';
import { FiPrinter, FiX } from 'react-icons/fi';
import { getImagenUrl } from '../../shared/api';
import { buildEscPos } from './escpos';
import { qzAvailable, qzPickPrinter, qzPrintRaw } from './qz';
import { buildTicketLines, mergeConfig, STYLE_CLASS, ticketCss } from './ticketLayout';
import TicketCanvas, { effectiveLogo } from './TicketCanvas';

const QZ_PRINTER_KEY = 'agorax_ticket_printer';

// Escapa HTML (anti-XSS en el doc.write del iframe/ventana de impresion).
const esc = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Construye el HTML del ticket (80mm) a partir de las lineas del layout compartido.
// Usa el mismo CSS/fuente que TicketCanvas para que la vista previa coincida.
function buildTicketHtml(recibo, modo, monedasSel, incluirIva, auto) {
  const cfg = mergeConfig(recibo?.ticket_config);
  const lines = buildTicketLines(recibo, modo, monedasSel, incluirIva);
  const logoUrl = getImagenUrl(effectiveLogo(recibo));

  const body = lines
    .map((ln) => {
      if (ln.kind === 'divider') {
        return `<div class="divider"></div>`;
      }
      if (ln.kind === 'pair') {
        const cls = `pair ${STYLE_CLASS[ln.style] || ''}`.trim();
        return `<div class="${cls}"><span class="left">${esc(ln.left)}</span><span class="fill${ln.dotted ? ' dots' : ''}"></span><span class="right">${esc(ln.right)}</span></div>`;
      }
      return `<div class="line ${STYLE_CLASS[ln.style] || ''}">${esc(ln.text)}</div>`;
    })
    .join('\n');

  return `<!doctype html><html><head><meta charset="utf-8"><title>Ticket</title>
<style>
@page { size: 80mm 210mm; margin: 0; }
html, body { margin: 0; padding: 0; }
body { width: 80mm; padding: 2mm; box-sizing: border-box; }
${ticketCss(cfg)}
</style></head><body>
<div class="agx-ticket">
${logoUrl ? `<div class="logo"><img src="${logoUrl}" alt=""/></div>` : ''}
${body}
</div>
${auto ? `<script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>` : ''}
</body></html>`;
}

export default function TicketImprimir({ recibo, modo, autoPrint, onClose }) {
  // IVA: para el ticket de entrega ya viene decidido (cobrar_impuesto). Para el
  // ticket de cobro (cuenta) el cajero lo elige aqui.
  const [incluirIva, setIncluirIva] = useState(() => !!recibo?.cobrar_impuesto);

  useEffect(() => {
    setIncluirIva(!!recibo?.cobrar_impuesto);
  }, [recibo]);

  // Imprime por window.print() (ventana nueva, patrón probado en QRMesaModal).
  const windowPrint = useCallback(
    (auto) => {
      const html = buildTicketHtml(recibo, modo, [], incluirIva, auto);

      let win = null;
      try {
        win = window.open('', '_blank', 'width=320,height=640');
      } catch {
        win = null;
      }

      if (win && win.document) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        if (!auto) {
          win.addEventListener('load', () => {
            setTimeout(() => {
              try {
                win.focus();
                win.print();
              } catch (e) {
                /* noop */
              }
            }, 300);
          });
        }
        return;
      }

      // Fallback iframe con ancho real para que renderice el contenido.
      const iframe = document.createElement('iframe');
      iframe.style.cssText =
        'position:fixed;left:-10000px;top:0;width:302px;height:600px;border:0;background:#fff;';
      document.body.appendChild(iframe);
      const fwin = iframe.contentWindow;
      const fdoc = fwin.document;
      fdoc.open();
      fdoc.write(html);
      fdoc.close();
      const cleanup = () => {
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 30000);
      };
      if (auto) {
        cleanup();
      } else {
        iframe.addEventListener('load', () => {
          setTimeout(() => {
            try {
              fwin.focus();
              fwin.print();
            } catch (e) {
              /* noop */
            }
          }, 300);
        });
        cleanup();
      }
    },
    [recibo, modo, incluirIva]
  );

  const imprimir = useCallback(
    async (auto) => {
      const bytes = buildEscPos(recibo, modo, [], incluirIva);
      try {
        const preferred = localStorage.getItem(QZ_PRINTER_KEY);
        const printer = await qzPickPrinter(preferred);
        if (printer) {
          await qzPrintRaw(printer, bytes);
          if (!preferred) localStorage.setItem(QZ_PRINTER_KEY, printer);
          setPrintStatus('ok');
          return; // impreso directo a la ticketera
        }
      } catch (e) {
        console.warn('QZ Tray no disponible, usando window.print():', e?.message || e);
      }
      setPrintStatus('fallback');
      windowPrint(auto);
    },
    [recibo, modo, incluirIva, windowPrint]
  );

  // Estado de la ticketera detectada por QZ Tray (para informar en la UI).
  const [printStatus, setPrintStatus] = useState('idle'); // idle | ok | fallback | noqz
  useEffect(() => {
    let mounted = true;
    qzAvailable()
      .then(async (ok) => {
        if (!mounted) return;
        if (!ok) {
          setPrintStatus('noqz');
          return;
        }
        try {
          const preferred = localStorage.getItem(QZ_PRINTER_KEY);
          const printer = await qzPickPrinter(preferred);
          if (mounted) setPrintStatus(printer ? 'ok' : 'noqz');
        } catch {
          if (mounted) setPrintStatus('noqz');
        }
      })
      .catch(() => mounted && setPrintStatus('noqz'));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (autoPrint && recibo) {
      const t = setTimeout(() => imprimir(true), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, recibo]);

  if (!recibo) return null;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          width: 400,
          maxWidth: '94%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e8eaef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>
              {modo === 'entrega' ? 'Ticket de Entrega' : 'Ticket de Cobro'}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{recibo.empresa || ''}</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}
          >
            <FiX size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {modo === 'cobro' && (parseFloat(recibo.impuesto) || 0) > 0 && (
            <button
              onClick={() => setIncluirIva((v) => !v)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 10,
                border: `1.5px solid ${incluirIva ? '#2563eb' : '#d1d5db'}`,
                background: incluirIva ? '#eff6ff' : '#fff',
                cursor: 'pointer',
                fontFamily: 'inherit',
                marginBottom: 14,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Incluir IVA en la cuenta</span>
              <span
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 999,
                  background: incluirIva ? '#2563eb' : '#d1d5db',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: incluirIva ? 20 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    transition: 'left 0.15s',
                  }}
                />
              </span>
            </button>
          )}

          <div
            style={{
              background: '#fafafa',
              border: '1px dashed #d1d5db',
              borderRadius: 10,
              padding: 14,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <TicketCanvas recibo={recibo} modo={modo} incluirIva={incluirIva} />
          </div>
        </div>

        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #e8eaef',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            flexShrink: 0,
          }}
        >
          {printStatus === 'noqz' && (
            <div style={{ fontSize: 11, color: '#b45309', background: '#fffbeb', borderRadius: 8, padding: '6px 10px', border: '1px solid #fde68a' }}>
              Ticketera no detectada. Instala y abre QZ Tray, o se usara la impresora del navegador.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => imprimir(false)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 12,
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <FiPrinter size={16} /> Imprimir
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '12px 18px',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
